import { Fragment, PureComponent, ReactNode } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { ThunkDispatch } from 'redux-thunk';
import { useHistory, useParams } from 'react-router';

import { GlobalState } from '../../store/reducers';

import { Actions } from '../../store/actions';
import { GO_TO_VITALS_DETAILS } from '../routes';
import AppHeader from '../../components/AppHeader';
import fetchPastVitals, { VitalHistory, PatientProfileData } from '../../api/fetchPastVitals';
import { getAgeString } from '../../store/utils';
import FullScreenLoader from '../../components/FullScreenLoader';
import { track } from '../../tracking/mixpanel';

interface Props extends RouteComponentProps {
    dispatch: ThunkDispatch<GlobalState, null, Actions>;
}

export const DELAY_DUE_TO_RIPPLE = 200;

type State = {
    vitalsHistory?: { [k: string]: VitalHistory };
    ptProfile?: PatientProfileData;
    fetchStatus: 'loading' | 'success' | 'failed';
};

class PrescriptionPad extends PureComponent<Props, State> {
    state: State = { fetchStatus: 'loading' };
    componentDidMount = async (): Promise<void> => {
        fetchPastVitals()
            .then((r) => {
                if (r.data) {
                    this.setState({
                        vitalsHistory: r.data.vitalData,
                        ptProfile: r.data.patientProfileData,
                        fetchStatus: 'success',
                    });
                } else {
                    this.goBack();
                }
            })
            .catch((e) => {
                this.goBack();
                return;
            });

        return;
    };

    onAddVitals = async () => {
        track('ADD_VITALS_BT_CLICK');
        try {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            typeof window?.Eka?.vitalsEntry === 'function' &&
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                (await window.Eka.vitalsEntry(null, '7'));
        } catch (e) {
            console.error(e);
        }
        return;
    };

    goBack = async () => {
        try {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            typeof window?.Eka?.closeActivity === 'function' && (await window.Eka.closeActivity());
        } catch (e) {
            console.error(e);
        }
        return;
    };

    render() {
        const { vitalsHistory, ptProfile, fetchStatus } = this.state;

        if (fetchStatus === 'loading') {
            return <FullScreenLoader />;
        }

        const age = ptProfile?.dob ? getAgeString({ dob: ptProfile.dob }, undefined, ' /') : '';

        return (
            <div className="flex flex-col h-full font-grotesk">
                <AppHeader
                    title={getPtName(ptProfile)}
                    subTitle={`${age || ''} ${ptProfile?.gen ? `${ptProfile?.gen} / ` : ''} ${
                        ptProfile?.mobile
                    }`}
                    goBack={this.goBack}
                />
                {vitalsHistory && <VitalsList vitalsHistory={vitalsHistory} />}

                <div className="flex items-center justify-between w-full px-16 py-12 shadow-bottom-sheet mt-34">
                    <div className="flex items-center justify-center w-full">
                        <button
                            onClick={this.onAddVitals}
                            className="px-64 py-12 primary-btn font-700 ripple"
                        >
                            Add Vitals
                        </button>
                    </div>
                </div>
            </div>
        );
    }
}

const TEMPERATURE_ICON = (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 512 512"
        width="16"
        height="16"
        className="fill-icons-brand"
    >
        <path d="M476.8 20.4c-37.5-30.7-95.5-26.3-131.9 10.2l-45.7 46 50.5 50.5c3.1 3.1 3.1 8.2 0 11.3l-11.3 11.3c-3.1 3.1-8.2 3.1-11.3 0l-50.4-50.5-45.1 45.4 50.3 50.4c3.1 3.1 3.1 8.2 0 11.3l-11.3 11.3c-3.1 3.1-8.2 3.1-11.3 0L209 167.4l-45.1 45.4L214 263c3.1 3.1 3.1 8.2 0 11.3l-11.3 11.3c-3.1 3.1-8.2 3.1-11.3 0l-50.1-50.2L96 281.1V382L7 471c-9.4 9.4-9.4 24.6 0 33.9 9.4 9.4 24.6 9.4 33.9 0l89-89h99.9L484 162.6c34.9-34.9 42.2-101.5-7.2-142.2z"></path>
    </svg>
);

const SPO2_ICON = (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 640 512"
        width="16"
        height="16"
        className="fill-icons-brand"
    >
        <path d="M636.11 390.15C614.44 308.85 580.07 231 534.1 159.13 511.98 124.56 498.03 96 454.05 96 415.36 96 384 125.42 384 161.71v60.11l-32.88-21.92a15.996 15.996 0 0 1-7.12-13.31V16c0-8.84-7.16-16-16-16h-16c-8.84 0-16 7.16-16 16v170.59c0 5.35-2.67 10.34-7.12 13.31L256 221.82v-60.11C256 125.42 224.64 96 185.95 96c-43.98 0-57.93 28.56-80.05 63.13C59.93 231 25.56 308.85 3.89 390.15 1.3 399.84 0 409.79 0 419.78c0 61.23 62.48 105.44 125.24 88.62l59.5-15.95c42.18-11.3 71.26-47.47 71.26-88.62v-87.49l-85.84 57.23a7.992 7.992 0 0 1-11.09-2.22l-8.88-13.31a7.992 7.992 0 0 1 2.22-11.09L320 235.23l167.59 111.72a7.994 7.994 0 0 1 2.22 11.09l-8.88 13.31a7.994 7.994 0 0 1-11.09 2.22L384 316.34v87.49c0 41.15 29.08 77.31 71.26 88.62l59.5 15.95C577.52 525.22 640 481.01 640 419.78c0-9.99-1.3-19.94-3.89-29.63z" />
    </svg>
);

const BP_ICON = (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 352 512"
        width="16"
        height="16"
        className="fill-icons-brand"
    >
        <path d="M205.22 22.09c-7.94-28.78-49.44-30.12-58.44 0C100.01 179.85 0 222.72 0 333.91 0 432.35 78.72 512 176 512s176-79.65 176-178.09c0-111.75-99.79-153.34-146.78-311.82zM176 448c-61.75 0-112-50.25-112-112 0-8.84 7.16-16 16-16s16 7.16 16 16c0 44.11 35.89 80 80 80 8.84 0 16 7.16 16 16s-7.16 16-16 16z"></path>
    </svg>
);

const SUGAR_ICON = (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 512 512"
        width="16"
        height="16"
        className="fill-icons-brand"
    >
        <path d="M239.1 7.5l-208 78c-18.7 7-31.1 25-31.1 45v225.1c0 18.2 10.3 34.8 26.5 42.9l208 104c13.5 6.8 29.4 6.8 42.9 0l208-104c16.3-8.1 26.5-24.8 26.5-42.9V130.5c0-20-12.4-37.9-31.1-44.9l-208-78C262 3.4 250 3.4 239.1 7.5zm16.9 45l208 78v.3l-208 84.5-208-84.5v-.3l208-78zM48 182.6l184 74.8v190.2l-184-92v-173zm232 264.9V257.4l184-74.8v172.9l-184 92z"></path>
    </svg>
);

export const ICON_MAP: { [k: string]: ReactNode } = {
    'v-4698691230': TEMPERATURE_ICON,
    'v-5175552214': SPO2_ICON,
    'v-1116794113': BP_ICON,
    'v-1116138711': BP_ICON,
};

type VLP = {
    vitalsHistory: { [k: string]: VitalHistory };
};

const VitalsList: React.FC<VLP> = ({ vitalsHistory }) => {
    const history = useHistory();

    const withRippleTimeout = (k: string, v: VitalHistory) => {
        setTimeout(() => {
            history.push({
                pathname: GO_TO_VITALS_DETAILS(k),
                state: { k, data: v },
            });
        }, DELAY_DUE_TO_RIPPLE);
    };

    const items = Object.entries(vitalsHistory);

    return (
        <Fragment>
            <div className="overflow-auto" style={{ height: 'calc(100% - 114px)' }}>
                {items.length === 0 ? (
                    <EmptyState />
                ) : (
                    items.map(([k, v]) => (
                        <Fragment>
                            <div
                                onClick={() => withRippleTimeout(k, v)}
                                className="flex items-center justify-between w-full p-16 ripple"
                            >
                                <div className="flex items-center space-x-12">
                                    <div className="flex items-center justify-center w-24 h-24">
                                        {ICON_MAP[k.toString()] || SUGAR_ICON}
                                    </div>
                                    <p className="text-text-primary leading-5  font-700">
                                        {v.name}
                                    </p>
                                </div>
                                <div className="flex items-center justify-center w-24 h-24">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 256 512"
                                        width="22"
                                        height="22"
                                        className="fill-icons-brand"
                                    >
                                        <path d="M17.525 36.465l-7.071 7.07c-4.686 4.686-4.686 12.284 0 16.971L205.947 256 10.454 451.494c-4.686 4.686-4.686 12.284 0 16.971l7.071 7.07c4.686 4.686 12.284 4.686 16.97 0l211.051-211.05c4.686-4.686 4.686-12.284 0-16.971L34.495 36.465c-4.686-4.687-12.284-4.687-16.97 0z"></path>
                                    </svg>
                                </div>
                            </div>
                            <div className="mx-16 border-b border-light"></div>
                        </Fragment>
                    ))
                )}
            </div>
        </Fragment>
    );
};

const EmptyState = () => {
    return (
        <div className="flex flex-col justify-center items-center space-y-8 h-full">
            <div>
                <svg viewBox="0 0 448 512" width="42" height="48" className="fill-neutral-3">
                    <path d="M400 64h-48V12c0-6.6-5.4-12-12-12h-8c-6.6 0-12 5.4-12 12v52H128V12c0-6.6-5.4-12-12-12h-8c-6.6 0-12 5.4-12 12v52H48C21.5 64 0 85.5 0 112v352c0 26.5 21.5 48 48 48h352c26.5 0 48-21.5 48-48V112c0-26.5-21.5-48-48-48zM48 96h352c8.8 0 16 7.2 16 16v48H32v-48c0-8.8 7.2-16 16-16zm352 384H48c-8.8 0-16-7.2-16-16V192h384v272c0 8.8-7.2 16-16 16zm-105.3-95.9c4.7 4.7 4.7 12.3 0 17l-5.7 5.7c-4.7 4.7-12.3 4.7-17 0l-48-48.2-48.1 48.1c-4.7 4.7-12.3 4.7-17 0l-5.7-5.7c-4.7-4.7-4.7-12.3 0-17l48.1-48.1-48.1-48.1c-4.7-4.7-4.7-12.3 0-17l5.7-5.7c4.7-4.7 12.3-4.7 17 0l48.1 48.1 48.1-48.1c4.7-4.7 12.3-4.7 17 0l5.7 5.7c4.7 4.7 4.7 12.3 0 17L246.6 336l48.1 48.1z"></path>
                </svg>
            </div>
            <div>
                <p className="text-neutral-3">No Vitals Recorded Yet</p>
            </div>
        </div>
    );
};

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
export default connect((state: GlobalState) => ({}))(PrescriptionPad);

function getPtName(ptProfile: PatientProfileData | undefined): string {
    return [ptProfile?.fn, ptProfile?.mn, ptProfile?.ln].filter(Boolean).join(' ');
}
